import { TypographyStyles } from '@/styles'
import { Button, Dialog, Link, List, Text } from '@/ui'

export const PayrollComplianceDialog = ({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <Dialog.Content size="sm" onDismiss={onClose}>
        <Dialog.Title>Payroll Compliance Requirements</Dialog.Title>

        <div className="flex flex-col gap-4">
          <div className="w-full">
            <div className={'scrollable bg-stone'}>
              <Text>
                As Heard is getting ready for the upcoming tax season,
                we&apos;re reaching out to all S corporations to verify that
                you&apos;re fulfilling all the necessary payroll
                responsibilities and meeting IRS and state regulations. For more
                information and to download the Payroll Audit Checklist,{' '}
                <Link
                  newPage
                  href="https://support.joinheard.com/hc/en-us/articles/19204189645719"
                >
                  refer to this article
                </Link>
                .
              </Text>

              <Text variant={TypographyStyles.H3}>
                Why do I need to set up payroll?
              </Text>

              <Text>
                As an S corp, while you&apos;re the owner of the business,
                you&apos;re also an employee. Therefore, you&apos;re required to
                set up payroll and pay yourself a reasonable salary.
              </Text>

              <Text variant={TypographyStyles.H3}>
                How can I ensure that I comply with all the necessary
                requirements?
              </Text>

              <Text>
                Please review our{' '}
                <Link
                  newPage
                  href="https://support.joinheard.com/hc/en-us/article_attachments/19203994461207"
                >
                  Payroll Audit Checklist
                </Link>{' '}
                for steps you’ll need to take to ensure you are compliant with
                federal and state requirements. By following these steps and
                providing the necessary information, you can ensure compliance
                with all required payroll regulations.
              </Text>

              <Text>
                Here are the key compliance requirements: <br />
                <List>
                  <List.Item>Run payroll within the calendar year</List.Item>
                  <List.Item>Set a reasonable salary</List.Item>
                  <List.Item>Provide health insurance</List.Item>

                  <List.Item>
                    Pay payroll taxes (both federal and state)
                    <List>
                      <List.Item>
                        Specify the amount of federal payroll taxes
                      </List.Item>

                      <List.Item>
                        Specify the amount of state payroll taxes
                      </List.Item>
                    </List>
                  </List.Item>

                  <List.Item>
                    Complete state registration
                    <List bulleted>
                      <List.Item>
                        If applicable, provide a list of the states where
                        registration has been completed
                      </List.Item>
                    </List>
                  </List.Item>
                </List>
              </Text>

              <Text>
                By following these steps and providing the necessary
                information, you can ensure compliance with all required payroll
                regulations.
              </Text>

              <Text variant={TypographyStyles.H3}>
                What are the implications if I haven&apos;t run payroll?
              </Text>

              <Text>
                Fortunately, there is still enough time to run payroll before
                the end of the calendar year. Between the time you elected your
                S corp status and now, it is possible you may receive notices
                about missing wage reports. In that case, it would simply mean
                working with the state and the IRS to submit $0 fillings since
                payroll was not run for those periods.
              </Text>

              <Text>
                Going forward, ensure payroll is set up and you are running it
                so that the payroll provider can prepare and submit these
                filings on your behalf. It is important to take immediate action
                to prevent any fines, penalties, and interest charges from the
                IRS when you file your annual tax return.
              </Text>

              <Text variant={TypographyStyles.H3}>
                How do I set up payroll if I haven’t already?
              </Text>

              <Text>
                If you haven’t set up payroll for your therapy practice,{' '}
                <Link newPage href="/payroll/welcome">
                  click here
                </Link>{' '}
                to access Heard Payroll. Feel free to{' '}
                <Link
                  newPage
                  href="https://www.joinheard.com/articles/how-to-set-up-payroll-for-your-therapy-practice"
                >
                  refer to this article
                </Link>{' '}
                for more information on how to set up Payroll for your Therapy
                Practice.
              </Text>

              <Text variant={TypographyStyles.H3}>
                How can I ensure Heard has all the documents needed for tax
                season?
              </Text>

              <Text>
                Please take a moment to ensure that the following compliance
                documents are uploaded to your Heard account. If you have not
                already done so, please upload these documents as soon as
                possible.
              </Text>

              <List>
                <List.Item>
                  CP261 Notice - S corporation approval letter from the IRS
                </List.Item>
                <List.Item>Form 2553 - S corporation election</List.Item>
                <List.Item>CP575 - EIN letter from the IRS</List.Item>
              </List>

              <Text>
                If you are unable to find any of the necessary documents, feel
                free to reach out to the IRS at 1-800-829-4933. When contacting
                them, please ensure that you have your Employer Identification
                Number (EIN) handy. You can expect to receive these documents by
                mail within a specific timeframe after making your request.
              </Text>
            </div>
          </div>

          <div className="w-full pt-0 md:p-0">
            <Link
              newPage
              href="https://support.joinheard.com/hc/en-us/article_attachments/19203994461207"
            >
              Payroll Audit Checklist
            </Link>
          </div>
        </div>
      </Dialog.Content>

      <Dialog.Footer>
        <Button onClick={onSuccess}>I understand</Button>
      </Dialog.Footer>
    </Dialog>
  )
}
